import { __rest } from 'tslib';
import { t } from '../../i18n.es.js';
import React, { useState, useEffect, useMemo } from 'react';
import { useModal, Row, Col, Checkbox, Quantity, styled, CommonStyles, Body } from '@teko/ds-desktop';
import Voucher from '../../assets/voucher.es.js';
import { debounce } from '@teko/utils';
import { ProductPrice } from '@teko/teko-ecommerce';
import { ProductInfo } from '../../Common/ProductInfo/ProductInfo.es.js';
import { ProductPriceCart } from '../../Common/ProductPriceCart/ProductPriceCart.es.js';
import { Gift } from '../../Common/Gift/Gift.es.js';

const CartLine = (_a) => {
    var _b, _c, _d, _e;
    var { product, onChangeQuantity, onRemove, onCheck, showCheck = false, error, onEditPrice, disableProductPromo, maximumValue = 9999, isMobile = false, isAllowEditPrice = false, isQuantityDecimal = false } = _a, props = __rest(_a, ["product", "onChangeQuantity", "onRemove", "onCheck", "showCheck", "error", "onEditPrice", "disableProductPromo", "maximumValue", "isMobile", "isAllowEditPrice", "isQuantityDecimal"]);
    const [productQuantity, setProductQuantity] = useState(product.quantity || 0);
    const precision = isQuantityDecimal ? 1 : 0;
    useEffect(() => {
        setProductQuantity(product.quantityDecimal);
    }, [product.quantityDecimal]);
    const { showModal } = useModal();
    const cartLineError = error;
    const handleRemoveFromCart = () => {
        showModal({
            title: t('Chú ý'),
            children: (React.createElement(Body, { level: 1, style: { textAlign: 'center' } }, t('Bạn muốn xoá sản phẩm này ra khỏi giỏ hàng?'))),
            onOk: onRemove,
            width: 400,
            okText: t('Đồng ý'),
            cancelText: t('Hủy bỏ'),
            alignFooter: 'center',
            centeredTitle: true,
            closable: false,
        });
    };
    const debounceChange = useMemo(() => debounce((value) => {
        onChangeQuantity(Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision));
    }, 1000), [onChangeQuantity, precision]);
    return (React.createElement(StyledCard, Object.assign({}, props),
        React.createElement(Row, { justify: "space-between", align: "middle" },
            React.createElement(Col, { flex: "4%" }, showCheck && (React.createElement(Checkbox, { checked: product.selected, onChange: onCheck }))),
            React.createElement(Col, { flex: "96%" },
                React.createElement(Row, { justify: "space-between" },
                    React.createElement(Col, { span: 6 },
                        React.createElement(ProductInfo, { product: product })),
                    React.createElement(Col, { span: 2, paddingRight: "s16" },
                        React.createElement(StyledProductPriceCart, { supplierRetailPrice: product.unitPrice, latestPrice: product.price, textColor: "textPrimary", align: "end", isPriceEditable: product.maxConsultationServiceFee > 0 && isAllowEditPrice, onEditPrice: onEditPrice, isMobile: isMobile })),
                    React.createElement(Col, { span: 2 },
                        React.createElement(QuantitySelect, null,
                            React.createElement(Quantity, { quantity: productQuantity, setQuantity: (value) => {
                                    if (value !== productQuantity) {
                                        setProductQuantity(value);
                                        debounceChange(value);
                                    }
                                }, onPlus: () => {
                                    if (productQuantity + 1 <= maximumValue) {
                                        setProductQuantity(productQuantity + 1);
                                        debounceChange(productQuantity + 1);
                                    }
                                }, onMinus: () => {
                                    if (productQuantity - 1 > 0) {
                                        setProductQuantity(productQuantity - 1);
                                        debounceChange(productQuantity - 1);
                                    }
                                }, max: maximumValue, min: Math.pow(10, -precision), decimalSeparator: ".", precision: precision, blurOnEnter: true }),
                            React.createElement(StyledButtonDelete, { level: 1, color: "link500", hoverColor: "error500", onClick: handleRemoveFromCart }, t('Xóa')),
                            cartLineError && (React.createElement(Error, { level: 1, color: "error500", weight: "regular", line: 2 }, cartLineError)))),
                    React.createElement(Col, { span: 2 },
                        React.createElement(ProductPrice, { latestPrice: product.rowTotal, size: "m", align: "end" }))))),
        React.createElement(Row, { justify: "end" },
            React.createElement(Col, { flex: "96%" }, (((_b = product === null || product === void 0 ? void 0 : product.gifts) === null || _b === void 0 ? void 0 : _b.length) > 0 || ((_c = product === null || product === void 0 ? void 0 : product.voucher) === null || _c === void 0 ? void 0 : _c.quantity) > 0) && (React.createElement(PromoInfo, null,
                ((_d = product === null || product === void 0 ? void 0 : product.gifts) === null || _d === void 0 ? void 0 : _d.length) > 0 &&
                    product.gifts.map((gift, index) => (React.createElement(StyledGift, { key: index, cartLineSelected: product.selected, image: {
                            src: gift.imageUrl,
                        }, text: `x${gift.quantity} ${gift.name}`, disableProductPromo: disableProductPromo && !gift.isDefault }))),
                ((_e = product === null || product === void 0 ? void 0 : product.voucher) === null || _e === void 0 ? void 0 : _e.quantity) > 0 && (React.createElement(StyledGift, { cartLineSelected: product.selected, image: { src: Voucher }, text: t('x{{ProductVoucherQuantity}} Voucher dùng cho đơn hàng sau', { ProductVoucherQuantity: product.voucher.quantity }), border: false }))))))));
};
const StyledProductPriceCart = styled(ProductPriceCart) `
  display: flex;
  justify-content: flex-end;
`;
const PromoInfo = styled.div `
  margin-top: 16px;
  padding: 8px 12px;
  background: #f8f8fc;
  border-radius: 4px;
`;
const QuantitySelect = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const StyledCard = styled.div `
  ${CommonStyles}
  padding: 1rem;
  background-color: white;
`;
const StyledButtonDelete = styled(Body) `
  margin-top: 12px;
  cursor: pointer;
`;
const StyledGift = styled(Gift) `
  margin: 0.5rem 0;
  background: #f8f8fc;
`;
const Error = styled(Body) `
  margin-top: 2px;
  position: relative;
`;

export { CartLine };
